import React, { useState, useEffect, useRef } from 'react';
import {
  Buildings, ShieldCheck, Info, ArrowClockwise, Check, X, ArrowRight,
} from '@phosphor-icons/react';
import {
  getBranchNames, getVacantStatus, updateVacantStatus, saveData,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import SectionLock from './SectionLock';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY } from '../utils/noData';

const LABEL_MAXLEN = 25; // dropdown label truncation limit — entire-app convention

// Truncates any dropdown label to the app-wide 25 char limit
function truncate(label) {
  if (!label) return '';
  return label.length > LABEL_MAXLEN ? `${label.slice(0, LABEL_MAXLEN)}…` : label;
}

function VacantReceiptPermissionForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchList, setBranchList] = useState([]);

  const [cao, setCao] = useState('');
  const [branchCode, setBranchCode] = useState('');
  // null = nothing chosen yet; true = Permission (accept); false = Rejection.
  const [isChecked, setIsChecked] = useState(null);
  const [reason, setReason] = useState('');

  const oldVacantStatusRef = useRef(null);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingBranchCode, setPendingBranchCode] = useState('');
  const [pendingBranchName, setPendingBranchName] = useState('');
  const [pendingOldStatus, setPendingOldStatus] = useState(null);
  const [pendingNewStatus, setPendingNewStatus] = useState(null);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchList(res || [])).catch(() => setBranchList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const preventExceedLength = (event, max) => {
    const input = event.target;
    if (
      input.value.length >= max &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'Tab'
    ) {
      event.preventDefault();
    }
  };

  const handleBranchChange = async (code) => {
    setCao(code);

    if (!code) {
      setBranchCode('');
      setIsChecked(null);
      oldVacantStatusRef.current = null;
      return;
    }

    const selected = branchList.find((x) => x.branch_code === code);
    if (!selected) return;

    setBranchCode(selected.branch_code);

    try {
      const res = await getVacantStatus();
      const list = res || [];
      const match = list.find((x) => x.branch_code === selected.branch_code);

      if (match) {
        const currentStatus = !match.is_vacant_full_receipt_mandatory;
        setIsChecked(currentStatus);
        oldVacantStatusRef.current = currentStatus;
      } else {
        // Clear the tick as well as the ref. Leaving it set would carry the
        // previous branch's permission over to this one, and a save would then
        // apply that stale value here.
        setIsChecked(null);
        oldVacantStatusRef.current = null;
        showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      }
    } catch {
      setIsChecked(null);
      oldVacantStatusRef.current = null;
    }
  };

  const reasonTrimLen = reason.trim().length;
  const caoInvalid = touched && !cao;
  const reasonInvalid = touched && (reasonTrimLen < 10 || reason.length > 200);

  const isFormValid = !!cao && isChecked !== null && reasonTrimLen >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);
    if (isChecked === null) {
      showToast('warn', 'Selection Required', 'Please choose Permission or Rejection.');
      return;
    }
    if (!isFormValid) return;

    const selected = branchList.find((x) => x.branch_code === branchCode);
    const newStatus = isChecked;
    const trimmedReason = reason.trim();

    const audit = {
      schemaName: branchCode,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'VACANT_RECEIPT_PERMISSION',
      oldData: {
        branchCode,
        branchName: selected?.branch_name || branchCode,
        vacantFullReceiptPermission: oldVacantStatusRef.current ?? null,
      },
      newData: {
        branchCode,
        branchName: selected?.branch_name || branchCode,
        vacantFullReceiptPermission: newStatus,
      },
      reason: trimmedReason,
    };

    setPendingAudit(audit);
    setPendingBranchCode(branchCode);
    setPendingBranchName(selected?.branch_name || branchCode);
    setPendingOldStatus(oldVacantStatusRef.current);
    setPendingNewStatus(newStatus);
    setPendingReason(trimmedReason);

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    setShowConfirmModal(false);
    setPendingAudit(null);
  };

  const confirmSave = async () => {
    if (!pendingAudit) return;

    setShowConfirmModal(false);
    setSaving(true);

    try {
      // pendingNewStatus, not isChecked: it is the same snapshot that built the
      // audit payload and that the confirm modal showed, so what is saved, what is
      // logged and what the user agreed to cannot drift apart.
      await updateVacantStatus(pendingBranchCode, pendingNewStatus);
      try {
        await saveData([pendingAudit]);
        setSaving(false);
        showToast('success', 'Success', 'Saved Successfully & Audit Saved', true);
        handleClear();
      } catch (err) {
        setSaving(false);
        console.error('Audit save failed:', err);
        showToast('warn', 'Partial Success', 'Saved, but audit failed', true);
      }
    } catch (err) {
      setSaving(false);
      console.error('Vacant status update failed:', err);
      // Surface what the API actually said — "no branch found with that code" is
      // worth reading, and the old hardcoded text threw it away.
      showToast('error', 'Save Failed', apiErrorMessage(err, 'Save Failed'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    setBranchCode('');
    setIsChecked(null);
    setReason('');
    oldVacantStatusRef.current = null;
    setPendingAudit(null);
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="vacant-receipt-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="vacant-receipt-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShieldCheck size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Branch Configuration</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Vacant Receipt Permission</div>
          </div>
        </div>

        {/* SECTION A: Branch Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Branch</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose the CAO branch to configure vacant receipt permission</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Buildings size={13} /> CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchList}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_code}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select CAO"
              emptyMessage="No branches found"
              error={caoInvalid}
              showClear
              testId="vacant-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>Branch Code</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={branchCode || ''} readOnly data-testid="vacant-branch-code" />
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!cao} hint="Select a branch in section A first">
        {/* SECTION B: Permission */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Permission</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Tick Permission to accept vacant receipts, or Rejection to stop accepting them — only one can be ticked</div>
          </div>
        </div>

        {/* Two separate, mutually exclusive checkboxes. isChecked is the single
            source of truth: Permission = true, Rejection = false, none = null. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label
            htmlFor="vacantPermission"
            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', margin: 0 }}
          >
            <input
              type="checkbox"
              id="vacantPermission"
              checked={isChecked === true}
              onChange={() => setIsChecked(isChecked === true ? null : true)}
              style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#059669' }}
              data-testid="vacant-permission-checkbox"
            />
            <span style={{ fontSize: '13.5px', color: '#111827' }}>Permission — Accept Vacant Receipt</span>
          </label>

          <label
            htmlFor="vacantRejection"
            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', margin: 0 }}
          >
            <input
              type="checkbox"
              id="vacantRejection"
              checked={isChecked === false}
              onChange={() => setIsChecked(isChecked === false ? null : false)}
              style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#DC2626' }}
              data-testid="vacant-rejection-checkbox"
            />
            <span style={{ fontSize: '13.5px', color: '#111827' }}>Rejection — Do Not Accept Vacant Receipt</span>
          </label>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!cao} hint="Select a branch in section A first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reasonTrimLen >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => preventExceedLength(e, 200)}
          data-testid="vacant-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reasonTrimLen === 0 ? 'Reason is required' : reasonTrimLen < 10 ? 'Minimum 10 characters required' : 'Maximum 200 characters allowed'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="vacant-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="vacant-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="vacant-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Permission Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You're about to update the vacant receipt permission for <strong>{truncate(pendingBranchName)}</strong>. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old Status</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>
                    {pendingOldStatus === null ? '—' : pendingOldStatus ? 'Accepted' : 'Not Accepted'}
                  </p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New Status</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>
                    {pendingNewStatus ? 'Accepted' : 'Not Accepted'}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} className="btn-secondary" data-testid="vacant-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="vacant-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VacantReceiptPermissionForm;